import React, { useState } from 'react';
import { Sun, X, Monitor, Moon } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

const OPTIONS = [
  { value: 'system', label: 'System', Icon: Monitor },
  { value: 'light', label: 'Light', Icon: Sun },
  { value: 'dark', label: 'Dark', Icon: Moon },
];

export function AppearanceSettingsButton({ className = '', compact = false }) {
  const [open, setOpen] = useState(false);
  const trigger = compact ? (
    <button
      onClick={() => setOpen(true)}
      data-testid="appearance-settings-trigger"
      className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-white font-medium text-[13px] shadow-md hover:brightness-105 transition-all ${className}`}
      style={{
        background: 'linear-gradient(90deg, #ffb347 0%, #f5a623 50%, #ff8c1a 100%)',
        boxShadow: '0 8px 18px -8px rgba(245, 140, 25, 0.5)',
      }}
    >
      <Sun className="w-4 h-4" strokeWidth={2.2} />
      Appearance
    </button>
  ) : (
    <button
      onClick={() => setOpen(true)}
      data-testid="appearance-settings-trigger"
      className={`inline-flex items-center gap-2.5 px-6 py-3 rounded-full text-white font-medium text-[14px] shadow-md hover:brightness-105 transition-all ${className}`}
      style={{
        background: 'linear-gradient(90deg, #ffb347 0%, #f5a623 50%, #ff8c1a 100%)',
        boxShadow: '0 10px 25px -10px rgba(245, 140, 25, 0.5)',
      }}
    >
      <Sun className="w-4 h-4" strokeWidth={2.2} />
      Appearance Settings
    </button>
  );

  return (
    <>
      {trigger}
      {open && <AppearanceSettingsModal onClose={() => setOpen(false)} />}
    </>
  );
}

function AppearanceSettingsModal({ onClose }) {
  const { theme, setTheme } = useTheme();

  // close on ESC
  React.useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="appearance-settings-title"
      data-testid="appearance-settings-modal"
    >
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />
      <div className="relative w-full max-w-md rounded-2xl bg-white dark:bg-[#111827] border border-gray-200 dark:border-white/10 shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-white/10">
          <div className="flex items-center gap-2">
            <Sun className="w-5 h-5 text-[#f5a623]" strokeWidth={2.2} />
            <h3
              id="appearance-settings-title"
              className="text-[15px] font-bold text-gray-900 dark:text-white"
            >
              Appearance Settings
            </h3>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            data-testid="appearance-settings-close"
            className="inline-flex items-center justify-center w-8 h-8 rounded-md bg-[#1a1a1a] text-white hover:bg-[#333] transition-colors"
          >
            <X className="w-4 h-4" strokeWidth={2.2} />
          </button>
        </div>

        <ul className="p-2">
          {OPTIONS.map(({ value, label, Icon }) => {
            const selected = theme === value;
            return (
              <li key={value}>
                <button
                  type="button"
                  onClick={() => setTheme(value)}
                  data-testid={`appearance-option-${value}`}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                    selected
                      ? 'bg-[#fef3e2] dark:bg-[#f5a623]/10'
                      : 'hover:bg-gray-50 dark:hover:bg-white/5'
                  }`}
                >
                  <span
                    className={`flex items-center justify-center w-5 h-5 rounded-full border-2 ${
                      selected ? 'border-[#f5a623]' : 'border-gray-300 dark:border-gray-600'
                    }`}
                  >
                    {selected && <span className="w-2.5 h-2.5 rounded-full bg-[#f5a623]" />}
                  </span>
                  <Icon
                    className={`w-4 h-4 ${selected ? 'text-[#f5a623]' : 'text-gray-500 dark:text-gray-400'}`}
                    strokeWidth={2}
                  />
                  <span
                    className={`text-[15px] font-medium ${
                      selected ? 'text-[#b06a02] dark:text-[#f5a623]' : 'text-gray-800 dark:text-gray-200'
                    }`}
                  >
                    {label}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
